import React, { useState, useRef, useEffect } from "react";
import {
  GoStar,
  GoStarFill
} from "react-icons/go";
import {
  FiFilter,
  FiTrash,
  FiDownload
} from "react-icons/fi";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import { motion, AnimatePresence } from "framer-motion";

const DropDown = ({ trigger, options }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button onClick={() => setOpen((prev) => !prev)} className="cursor-pointer">
        {trigger}
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            key="dropdown"
            initial="closed"
            animate="open"
            exit="closed"
            variants={wrapperVariants}
            style={{ originY: "top", right: 0 }}
            className="flex flex-col gap-2 p-2 rounded-lg bg-white shadow-xl absolute top-[120%] right-0 w-56 overflow-hidden z-50"
          >
            {options.map((option, i) => (
              <Option
                key={i}
                setOpen={setOpen}
                Icon={option.icon}
                text={option.label}
                onClick={option.onClick}
              />
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

const Option = ({ text, Icon, onClick }) => (
  <motion.li
    variants={itemVariants}
    onClick={(e) => {
      e.stopPropagation();
      if (onClick) onClick();
    }}
    className="flex items-center gap-2 w-full p-2 text-xs font-medium whitespace-nowrap rounded-md text-slate-700 transition-colors cursor-pointer hover:bg-[#d6c8dc] hover:text-[#3c0f55]"
  >
    <motion.span variants={actionIconVariants}>{Icon}</motion.span>
    <span>{text}</span>
  </motion.li>
);

const wrapperVariants = {
  open: {
    scaleY: 1,
    opacity: 1,
    transition: { when: "beforeChildren", staggerChildren: 0.05, duration: 0.2 },
  },
  closed: {
    scaleY: 0,
    opacity: 0,
    transition: { when: "afterChildren", duration: 0.15 },
  },
};

const itemVariants = {
  open: { opacity: 1, y: 0 },
  closed: { opacity: 0, y: -10 },
};

const actionIconVariants = {
  open: { scale: 1 },
  closed: { scale: 0 },
};

const clientesData = [
  { name: "Ativos", value: 652, color: "#3f0f52" },
  { name: "Inativos", value: 150, color: "#e6d0e9" },
];

const tarefasData = [
  { name: "Segunda", value: 42, color: "#3f0f52" },
  { name: "Terça", value: 35, color: "#3330bc" },
  { name: "Quarta", value: 25, color: "#673b8e" },
  { name: "Quinta", value: 30, color: "#330ccb" },
  { name: "Sexta", value: 30, color: "#a04ccb" },
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 shadow-md rounded-lg border border-gray-100">
        <p className="font-semibold text-sm text-gray-800">{data.name}</p>
        <p className="text-sm">
          <span className="font-medium" style={{ color: data.color }}>
            {data.value}
          </span>{" "}
          tarefas
        </p>
      </div>
    );
  }
  return null;
};

const CustomBarLegend = () => (
  <div className="flex justify-center mt-4 space-x-6">
    {tarefasData.map((entry, index) => (
      <div key={index} className="flex items-center">
        <div
          className="w-3 h-3 rounded-full mr-2"
          style={{ backgroundColor: entry.color }}
        />
        <span className="text-sm text-gray-700">{entry.name}</span>
      </div>
    ))}
  </div>
);

const PieLegend = () => (
  <div className="flex justify-center mt-4 space-x-6">
    {clientesData.map((entry, index) => (
      <div key={index} className="flex items-center">
        <div
          className="w-3 h-3 rounded-full mr-2"
          style={{ backgroundColor: entry.color }}
        />
        <span className="text-sm text-gray-700">{entry.name}</span>
      </div>
    ))}
  </div>
);

const renderCustomizedLabel = () => null;

const renderGraphic = (graphic) => {
  switch (graphic.tipo) {
    case "pizza":
      return (
        <>
         <ResponsiveContainer width="100%" height="70%">
          <PieChart>
            <Pie
              data={clientesData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="80%"
              paddingAngle={6}
              cornerRadius={10}
              stroke="none"
              labelLine={false}
              startAngle={90}
              endAngle={450}
            >
              {clientesData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-gray-800 font-bold"
              style={{ fontSize: 'clamp(16px, 5vw, 24px)', pointerEvents: 'none' }}
            >
              652
            </text>
          </PieChart>
        </ResponsiveContainer>
        <PieLegend />
        </>
      );
    case "barra":
      return (
        <>
          <ResponsiveContainer width="100%" height="70%">
            <BarChart data={tarefasData} barSize={35}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {tarefasData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <CustomBarLegend />
        </>
      );
    case "linha":
      return (
        <>
          <ResponsiveContainer width="100%" height="70%">
            <LineChart data={tarefasData}>
              <XAxis dataKey="name" />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="value" stroke="#672c71" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
          <CustomBarLegend />
        </>
      );
    case "area":
      return (
        <>
          <ResponsiveContainer width="100%" height="70%">
            <AreaChart data={tarefasData}>
              <XAxis dataKey="name" />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#672c71"
                fill="#e6d0e9"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
          <CustomBarLegend />
        </>
      );
    default:
      return <div className="text-sm text-gray-400">Tipo não suportado</div>;
  }
};

const GraphicCard = ({
  graphic,
  onDelete,
  isStarred = false,
  onToggleStar,
  resizable = false,
  width = 450,
  height = 300,
  onResize,
}) => {
  const [formData, setFormData] = useState({ fases: [] });

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      fases: checked
        ? [...prev.fases, name]
        : prev.fases.filter((f) => f !== name),
    }));
  };

  const faseOptions = [
    { title: "Lead quente", name: "lead_quente" },
    { title: "Negociação", name: "negociacao" },
    { title: "Abertura vendida", name: "abertura_vendida" },
    { title: "Ganho (falta contrato)", name: "ganho" },
    { title: "Contrato Assinado", name: "contrato_assinado" },
    { title: "Perdido", name: "perdido" },
    { title: "Não Convertido", name: "nao_convertido" },
    { title: "Arquivo", name: "arquivo" },
  ];

  const handleExport = async (format) => {
    const node = document.getElementById(`graphic-${graphic.id}`);
    if (!node) return;

    if (format === "jpg") {
      const canvas = await html2canvas(node);
      canvas.toBlob((blob) => {
        if (blob) saveAs(blob, `${graphic.titulo || "grafico"}.jpg`);
      }, "image/jpeg");
    } else if (format === "svg") {
      const svg = node.querySelector("svg");
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([svgData], { type: "image/svg+xml" });
        saveAs(blob, `${graphic.titulo || "grafico"}.svg`);
      }
    }
  };

  const Wrapper = resizable ? ResizableBox : "div";

  const wrapperProps = resizable
    ? {
        width,
        height,
        minConstraints: [500, 350],
        resizeHandles: ["se"],
        onResizeStop: onResize,
        className: "bg-white rounded-2xl border border-gray-200 shadow-sm p-6",
      }
    : {
        className: "bg-white rounded-2xl border border-gray-200 shadow-sm p-6",
      };

  return (
    <Wrapper {...wrapperProps}>
      <div id={`graphic-${graphic.id}`} style={{ width: "100%", height: "100%" }}>
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {graphic.titulo || "Gráfico"}
            </h3>
            {(graphic.linha || graphic.coluna || graphic.metrica) && (
              <p className="text-sm text-gray-500 mt-1">
                {(graphic.linha || "—") + " x " + (graphic.coluna || "—") + " x " + (graphic.metrica || "—")}
              </p>
            )}
          </div>
          <div className="flex gap-2 text-[#3f1057] items-start">
            <DropDown
              trigger={<FiFilter title="Filtrar fases" />}
              options={faseOptions.map((opt) => ({
                label: opt.title,
                icon: (
                  <input
                    type="checkbox"
                    name={opt.name}
                    checked={formData.fases.includes(opt.name)}
                    onChange={handleCheckboxChange}
                    className="accent-[#3f0f52]"
                  />
                ),
                onClick: () => {},
              }))}
            />
            {onDelete && (
              <FiTrash className="cursor-pointer" onClick={onDelete} title="Deletar gráfico" />
            )}
            <DropDown
              trigger={<FiDownload title="Exportar" />}
              options={[
                {
                  label: "Salvar como JPG",
                  onClick: () => handleExport("jpg"),
                  icon: <FiDownload />,
                },
                {
                  label: "Salvar como SVG",
                  onClick: () => handleExport("svg"),
                  icon: <FiDownload />,
                },
              ]}
            />
            {onToggleStar && (
              <button
                onClick={onToggleStar}
                className="text-gray-400 hover:text-[#672c71]"
                title={isStarred ? "Remover dos favoritos" : "Adicionar aos favoritos"}
              >
                {isStarred ? <GoStarFill color="#672c71" /> : <GoStar color="#672c71" />}
              </button>
            )}
          </div>
        </div>
        {renderGraphic(graphic)}
      </div>
    </Wrapper>
  );
};

export default GraphicCard;
